import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Code,
  Key,
  ExternalLink,
  Copy,
  CheckCircle,
  PlayCircle,
  FileText,
  Globe,
  Webhook
} from "lucide-react";
import Layout from "@/components/Layout";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const ApiDocumentation = () => {
  const { t } = useTranslation();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const codeExamples = {
    createProject: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/project \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "base_video_url": "https://www.youtube.com/watch?v=example",
    "webhook_url": "https://your-webhook.com/callback",
    "name": "My Project",
    "format": "auto",
    "language": "pt-BR",
    "prompt": ""
  }'`,

    listProjects: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/projects?limit=10&offset=0' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,

    getProject: `curl --request GET \\
  --url https://api.autoclipper.live/api/v1/project/PROJECT_ID \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,

    listCommunityLibraries: `curl --request GET \\
  --url 'https://api.autoclipper.live/api/v1/community/libraries?limit=10&sort=remixes' \\
  --header 'Authorization: API-Key YOUR_API_KEY'`,

    remixCommunityLibrary: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/community/library/LIBRARY_ID/remix \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "webhook_url": "https://your-webhook.com/callback",
    "auto_export": true
  }'`,

    createProjectClip: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/project/PROJECT_ID/clip \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "start_time": 124.3,
    "end_time": 167.8,
    "name": "Best part",
    "auto_render": true
  }'`,

    renderClip: `curl --request POST \\
  --url https://api.autoclipper.live/api/v1/clip/CLIP_ID/render \\
  --header 'Authorization: API-Key YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{}'`
  };

  const features = [
    {
      icon: PlayCircle,
      title: t('apiDocumentation.features.autoClipGeneration'),
      description: t('apiDocumentation.features.autoClipGenerationDescription')
    },
    {
      icon: Globe,
      title: t('apiDocumentation.features.multiLanguageSupport'),
      description: t('apiDocumentation.features.multiLanguageSupportDescription')
    },
    {
      icon: CheckCircle,
      title: t('apiDocumentation.features.realtimeUpdates'),
      description: t('apiDocumentation.features.realtimeUpdatesDescription')
    },
    {
      icon: Webhook,
      title: t('apiDocumentation.features.webhookNotifications'),
      description: t('apiDocumentation.features.webhookNotificationsDescription')
    },
    {
      icon: FileText,
      title: t('apiDocumentation.features.multipleFormats'),
      description: t('apiDocumentation.features.multipleFormatsDescription')
    }
  ];

  const quickStartSteps = [
    {
      step: 1,
      title: t('apiDocumentation.step1Title'),
      description: t('apiDocumentation.step1Description'),
      action: (
        <Button asChild variant="outline" size="sm">
          <Link to="/api-keys">
            <Key className="w-4 h-4 mr-2" />
            {t('apiDocumentation.getApiKey')}
          </Link>
        </Button>
      )
    },
    {
      step: 2,
      title: t('apiDocumentation.step2Title'),
      description: t('apiDocumentation.step2Description'),
      action: (
        <Button variant="outline" size="sm">
          <Code className="w-4 h-4 mr-2" />
          {t('apiDocumentation.tryExample')}
        </Button>
      )
    },
    {
      step: 3,
      title: t('apiDocumentation.step3Title'),
      description: t('apiDocumentation.step3Description'),
      action: (
        <Button asChild variant="outline" size="sm">
          <Link to="/api-reference">
            <BookOpen className="w-4 h-4 mr-2" />
            {t('apiDocumentation.viewDocs')}
          </Link>
        </Button>
      )
    }
  ];

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent text-white">
            {t('apiDocumentation.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('apiDocumentation.subtitle')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild>
              <Link to="/api-getting-started">
                <PlayCircle className="w-4 h-4 mr-2" />
                {t('apiDocumentation.getStarted')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/api-playground">
                <Code className="w-4 h-4 mr-2" />
                {t('apiDocumentation.tryPlayground')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/api-reference">
                <BookOpen className="w-4 h-4 mr-2" />
                {t('apiDocumentation.apiReference')}
              </Link>
            </Button>
          </div>
        </div>

        {/* API Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-bold text-white">13</div>
              <div className="text-sm text-muted-foreground">{t('apiDocumentation.apiEndpoints')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-bold text-white">REST</div>
              <div className="text-sm text-muted-foreground">{t('apiDocumentation.apiArchitecture')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-2xl font-bold text-white">JSON</div>
              <div className="text-sm text-muted-foreground">{t('apiDocumentation.responseFormat')}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5" />
              {t('apiDocumentation.quickStart')}
            </CardTitle>
            <CardDescription>
              {t('apiDocumentation.quickStartDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              {quickStartSteps.map((step) => (
                <div key={step.step} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                      {step.step}
                    </div>
                    <h3 className="font-semibold">{step.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                  {step.action}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiDocumentation.keyFeatures')}</CardTitle>
            <CardDescription>
              {t('apiDocumentation.keyFeaturesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Code Examples */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiDocumentation.codeExamples')}</CardTitle>
            <CardDescription>
              {t('apiDocumentation.codeExamplesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="create" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3 md:grid-cols-7">
                <TabsTrigger value="create">{t('apiDocumentation.createProject')}</TabsTrigger>
                <TabsTrigger value="list">{t('apiDocumentation.listProjects')}</TabsTrigger>
                <TabsTrigger value="get">{t('apiDocumentation.getProject')}</TabsTrigger>
                <TabsTrigger value="community">Community</TabsTrigger>
                <TabsTrigger value="remix">Remix</TabsTrigger>
                <TabsTrigger value="create-clip">Manual Clip</TabsTrigger>
                <TabsTrigger value="render-clip">Render Clip</TabsTrigger>
              </TabsList>

              <TabsContent value="create" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">POST /api/v1/project</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(codeExamples.createProject, 'create')}
                  >
                    {copiedCode === 'create' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.createProject}</code>
                </pre>
              </TabsContent>

              <TabsContent value="list" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">GET /api/v1/projects</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(codeExamples.listProjects, 'list')}
                  >
                    {copiedCode === 'list' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.listProjects}</code>
                </pre>
              </TabsContent>

              <TabsContent value="get" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">GET /api/v1/project/:id</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(codeExamples.getProject, 'get')}
                  >
                    {copiedCode === 'get' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.getProject}</code>
                </pre>
              </TabsContent>

              <TabsContent value="community" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">GET /api/v1/community/libraries</Badge>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(codeExamples.listCommunityLibraries, 'community')}>
                    {copiedCode === 'community' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.listCommunityLibraries}</code>
                </pre>
              </TabsContent>

              <TabsContent value="remix" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">POST /api/v1/community/library/:id/remix</Badge>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(codeExamples.remixCommunityLibrary, 'remix')}>
                    {copiedCode === 'remix' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.remixCommunityLibrary}</code>
                </pre>
              </TabsContent>

              <TabsContent value="create-clip" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">POST /api/v1/project/:id/clip</Badge>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(codeExamples.createProjectClip, 'create-clip')}>
                    {copiedCode === 'create-clip' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.createProjectClip}</code>
                </pre>
              </TabsContent>

              <TabsContent value="render-clip" className="space-y-2">
                <div className="flex items-center justify-between">
                  <Badge variant="outline">POST /api/v1/clip/:id/render</Badge>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(codeExamples.renderClip, 'render-clip')}>
                    {copiedCode === 'render-clip' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{codeExamples.renderClip}</code>
                </pre>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Resources */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiDocumentation.additionalResources')}</CardTitle>
            <CardDescription>
              {t('apiDocumentation.additionalResourcesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-getting-started">
                  <div className="flex items-start gap-3">
                    <PlayCircle className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiDocumentation.gettingStartedGuide')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiDocumentation.gettingStartedGuideDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-playground">
                  <div className="flex items-start gap-3">
                    <Code className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiDocumentation.interactivePlayground')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiDocumentation.interactivePlaygroundDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-reference">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiDocumentation.apiReferenceTitle')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiDocumentation.apiReferenceDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <a href="/openapi.json" target="_blank" rel="noopener noreferrer">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium flex items-center gap-1">
                        {t('apiDocumentation.openApiSpec')}
                        <ExternalLink className="w-3 h-3" />
                      </div>
                      <div className="text-sm text-muted-foreground">{t('apiDocumentation.openApiSpecDescription')}</div>
                    </div>
                  </div>
                </a>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-keys">
                  <div className="flex items-start gap-3">
                    <Key className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiDocumentation.manageApiKeys')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiDocumentation.manageApiKeysDescription')}</div>
                    </div>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ApiDocumentation;
